import React, { FC, useContext, useState } from "react";

// interfaces, helpers, & children components
import { Alert } from "components/common/Alert/Alert";
import { CartRemoveItemSvg } from "components/icons/CartRemoveItemSvg";
import { DiscountItemSvg } from "components/icons/DiscountItemSvg";
import { ITEM_BRAND } from "config";
import { CartContext } from "context/cart";
import { LocalizationContext } from "context/localization";
import { useTranslationContext } from "context/translation";
import { getEcommerceWithValue } from "helpers/analyticsLogger";
import { useCartAnalyticsEvents } from "hooks/analytics/useCartAnalyticsEvents";
import Link from "next/link";
import { CartItemProps } from "../../common/types";
import { FlagAndNote } from "../FlagAndNote";
import { ItemImage } from "../ItemImage";
import { QuantitySelector } from "../QuantitySelector";
import st from "./CartItem.module.scss";

export const CartItem: FC<CartItemProps> = ({
  blockedItem,
  cmsProducts,
  currentUserId,
  handleLogging,
  isGuest,
  isLink = true,
  itemImgCdn,
  itemImgSrc,
  lineItem,
  logCartLineItemQtyChangedEvent,
  maxCartQty,
  small,
  currency,
}) => {
  const { cart, updateCartItem } = useContext(CartContext);
  const { formatPriceByLocale } = useContext(LocalizationContext);
  const { remove } = useTranslationContext();
  const { addToCartEvent, removeFromCartEvent } = useCartAnalyticsEvents();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  const {
    item,
    item: { gtin, id, inventoryQty, isOutOfStock, title },
    quantity,
    subscriptionIntervalInDays,
    subtotal,
    perUnitPrice,
    strikethroughUnitPrice,
  } = lineItem;

  const formattedSubtotal = formatPriceByLocale(subtotal, true, true);
  const maxQty = inventoryQty ? Math.min(inventoryQty, maxCartQty) : maxCartQty;

  const itemListName = "Cart";
  const createEcommerceObject = (quantity: number) => {
    return getEcommerceWithValue({
      currency,
      itemListName,
      items: [
        {
          itemId: id,
          gtin: gtin,
          itemName: title,
          itemBrand: ITEM_BRAND,
          itemCategory: cmsProduct?.category?.analyticsCategoryName,
          price: perUnitPrice / 100,
          itemListName,
          currency,
          index: 0,
          quantity: quantity,
        },
      ],
    });
  };

  const updateQty = async (newQuantity: number, isIncrese?: boolean) => {
    if (!id) return;
    try {
      setLoading(true);
      const ecommerce = createEcommerceObject(1);

      const newCart = await updateCartItem({
        productId: id,
        quantity: newQuantity,
        subscriptionIntervalInDays: subscriptionIntervalInDays || null,
        analyticsDetails: ecommerce,
      });
      logCartLineItemQtyChangedEvent &&
        logCartLineItemQtyChangedEvent(
          currentUserId,
          cart || {},
          item,
          quantity,
          newQuantity,
          subscriptionIntervalInDays || null,
        );
      if (isIncrese) {
        addToCartEvent(newCart, ecommerce);
      } else {
        removeFromCartEvent(newCart, ecommerce);
      }
    } catch (err) {
      setError("Something went wrong. Please try again");
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!id) return;
    const ecommerce = createEcommerceObject(quantity);

    const newCart = await updateCartItem({
      productId: id,
      quantity: 0,
      subscriptionIntervalInDays: null,
    });
    removeFromCartEvent(newCart, ecommerce);
  };
  const cmsProduct = cmsProducts?.find((product) => product.gtin === gtin);

  const outOfStock = isOutOfStock || !inventoryQty || blockedItem?.isOutOfStock;
  const lowStock =
    (!!inventoryQty && quantity > inventoryQty) ||
    (blockedItem?.isLowStock && !!blockedItem?.inventoryQty);
  const remainingInventory = blockedItem?.inventoryQty || inventoryQty || 0;

  const pdpHref = `/${cmsProduct?.category?.slug}/${cmsProduct?.slug}`;

  /*
    https://pepsico-ecomm.atlassian.net/browse/PSU-1598
    This logic should be moved to the BE to make it consistent with checkout experience
    and to avoid duplication of logic in the FE
    We will couple this with a discount label below the product to indicate the promotion
    that triggers this discount in a seprate ticket: https://pepsico-ecomm.atlassian.net/browse/PSU-1596
  */
  const getPriceWithoutDiscount = () => {
    if (!strikethroughUnitPrice || strikethroughUnitPrice === perUnitPrice) {
      return;
    }
    return (
      <div className={st.strikethroughPrice}>
        {formatPriceByLocale(strikethroughUnitPrice * quantity, true, true)}
      </div>
    );
  };

  return (
    <div
      className="flex justify-start items-start gap-4 py-6"
      data-testid="cart-item"
    >
      <ItemImage
        productLink={pdpHref}
        isLink={isLink}
        itemImgCdn={itemImgCdn}
        itemImgSrc={itemImgSrc}
        productTitle={title || ""}
        small={small}
        handleLogging={handleLogging}
      />
      <div className="flex flex-col flex-1 justify-start items-start gap-6">
        <div className="flex flex-col gap-1 w-full">
          {cmsProduct?.aboveTitlePromo && (
            <div
              className={st.aboveTitlePromo}
              style={{
                color: cmsProduct.aboveTitlePromo.textColor?.hex ?? "inherit",
              }}
            >
              {cmsProduct.aboveTitlePromo.text}
            </div>
          )}
          <div className="flex w-full gap-1 md:gap-4 justify-between md:justify-start">
            <div className="flex-col items-start gap-1 inline-flex md:w-5/12">
              <Link href={pdpHref} onClick={handleLogging}>
                <div className=" text-zinc-900 text-base font-semibold leading-[21px] text-wrap">
                  {title}
                </div>
              </Link>
            </div>
            <div className="hidden md:block flex-1">
              <QuantitySelector
                disabled={loading}
                maxQty={maxQty}
                outOfStock={!!outOfStock}
                quantity={quantity}
                updateQty={updateQty}
              />
            </div>
            <div className="flex flex-col justify-center items-end gap-1">
              <div className={st.priceContainer}>
                {getPriceWithoutDiscount()}
                <h4 className="text-right text-gray-900 text-base font-semibold leading-[21px]">
                  {formattedSubtotal}
                </h4>
              </div>
              <p
                onClick={handleRemove}
                className="text-right text-blue-950 text-xs underline cursor-pointer hidden md:block"
              >
                {remove}
              </p>
            </div>
          </div>
          {cmsProduct?.belowTitlePromo && (
            <div
              className={st.aboveTitlePromo}
              style={{
                color: cmsProduct.belowTitlePromo.textColor?.hex ?? "inherit",
              }}
            >
              <DiscountItemSvg /> {cmsProduct.belowTitlePromo.text}
            </div>
          )}
        </div>
        <div className="flex w-full gap-2 justify-between md:hidden items-center">
          <QuantitySelector
            disabled={loading}
            maxQty={maxQty}
            outOfStock={!!outOfStock}
            quantity={quantity}
            updateQty={updateQty}
          />
          <div onClick={handleRemove} className="cursor-pointer">
            <CartRemoveItemSvg />
          </div>
        </div>
        <FlagAndNote
          outOfStock={outOfStock}
          lowStock={lowStock}
          remainingInventory={remainingInventory}
          product={item}
          quantity={quantity}
          inventoryQty={inventoryQty || 0}
          blockedItem={blockedItem}
          updateCartItem={updateCartItem}
          subscriptionIntervalInDays={subscriptionIntervalInDays || null}
          isGuest={isGuest}
        />
        {error && <Alert type="error">{error}</Alert>}
      </div>
    </div>
  );
};
